import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useData } from '../../../contexts/DataContext';
import { useAuth } from '../../../contexts/AuthContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../../common/SafeIcon';
import { generateInvoicePDF } from '../../../utils/invoiceGenerator';
import { motion, AnimatePresence } from 'framer-motion';

const { FiArrowLeft, FiFileText, FiDownload, FiInfo, FiCheckCircle, FiClock, FiAlertCircle, FiCreditCard, FiPackage, FiX } = FiIcons;

const InvoiceArchive = ({ isEmbedded = false }) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { companies, updateCompany, invoices: allInvoices, INVOICE_SENDER } = useData();
  
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const company = companies.find(c => c.id === user?.companyId);
  const invoices = (allInvoices || [])
    .filter(inv => inv && inv.companyId === user?.companyId)
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  const getStatusBadge = (status) => {
    switch (status) {
      case 'paid': return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300"><SafeIcon icon={FiCheckCircle} className="mr-1" /> Betalt</span>;
      case 'overdue': return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300"><SafeIcon icon={FiAlertCircle} className="mr-1" /> Forfalt</span>;
      default: return <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-300"><SafeIcon icon={FiClock} className="mr-1" /> Ubetalt</span>;
    }
  };

  const handleDownload = (invoice) => {
    if (company) {
      generateInvoicePDF(invoice, company, INVOICE_SENDER);
    }
  };

  const handleCancelSubscription = async () => {
    setIsProcessing(true);
    setTimeout(async () => {
      await updateCompany(company.id, {
        status: 'cancellation_pending',
        cancellationDate: new Date().toISOString()
      });
      setIsProcessing(false);
      setShowCancelModal(false);
      alert('Abonnementet er nå sagt opp. Du vil motta en bekreftelse på e-post.');
    }, 1500);
  };

  return (
    <div className={isEmbedded ? "h-full" : "min-h-screen bg-gray-50 dark:bg-gray-900"}>
      {!isEmbedded && (
        <header className="bg-white dark:bg-gray-800 shadow-sm border-b border-gray-200 dark:border-gray-700">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/company/dashboard')} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors text-gray-600 dark:text-gray-300">
                <SafeIcon icon={FiArrowLeft} />
              </button>
              <div>
                <h1 className="text-xl font-bold text-gray-900 dark:text-white">Faktura og Abonnement</h1>
                <p className="text-sm text-gray-500 dark:text-gray-400">Oversikt over betalinger og avtaler</p>
              </div>
            </div>
          </div>
        </header>
      )}

      <div className={isEmbedded ? "" : "max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"}>
        
        {/* Subscription Management Section */}
        {company && (
          <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-6 mb-8 shadow-sm">
            <div className="flex flex-col md:flex-row justify-between items-center gap-6">
              <div className="flex items-center gap-4">
                <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl text-blue-600 dark:text-blue-400">
                  <SafeIcon icon={FiPackage} className="text-2xl" />
                </div>
                <div>
                  <h2 className="text-lg font-bold text-gray-900 dark:text-white">Ditt Abonnement</h2>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Nåværende pakke: <span className="font-semibold text-gray-900 dark:text-white capitalize">{company.subscription || 'Standard'}</span>
                  </p>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    Månedspris: <span className="font-semibold text-gray-900 dark:text-white">{(company.monthly_fee || company.monthlyFee || 990).toLocaleString()} kr</span>
                  </p>
                </div>
              </div>

              <div className="flex gap-3">
                {company.status === 'cancellation_pending' ? (
                  <div className="px-4 py-2 bg-yellow-100 text-yellow-800 rounded-lg text-sm font-bold border border-yellow-200">
                    Oppsigelse registrert
                  </div>
                ) : (
                  <button 
                    onClick={() => setShowCancelModal(true)} 
                    className="px-4 py-2 border border-red-200 text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-colors"
                  >
                    Si opp abonnement
                  </button>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Invoice List */}
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm border border-gray-100 dark:border-gray-700 overflow-hidden">
          <div className="p-4 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
            <h3 className="font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <SafeIcon icon={FiFileText} /> Fakturaarkiv
            </h3>
          </div>
          
          {invoices.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-gray-50 dark:bg-gray-700/50 border-b border-gray-200 dark:border-gray-700 text-xs uppercase text-gray-500 dark:text-gray-400 font-semibold">
                  <tr>
                    <th className="px-6 py-4">Fakturanr</th>
                    <th className="px-6 py-4">Dato</th>
                    <th className="px-6 py-4">Forfall</th>
                    <th className="px-6 py-4">Beløp</th>
                    <th className="px-6 py-4">Status</th>
                    <th className="px-6 py-4 text-right">Handling</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
                  {invoices.map((invoice) => (
                    <tr key={invoice.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                      <td className="px-6 py-4 font-mono text-sm text-gray-900 dark:text-gray-200">
                        {invoice.invoiceNumber}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(invoice.date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">
                        {new Date(invoice.dueDate).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 font-medium text-gray-900 dark:text-white">
                        {(invoice.totalAmount || 0).toLocaleString('no-NO')} kr
                      </td>
                      <td className="px-6 py-4">
                        {getStatusBadge(invoice.status)}
                      </td>
                      <td className="px-6 py-4 text-right">
                        <button 
                          onClick={() => handleDownload(invoice)}
                          className="text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 font-medium text-sm flex items-center justify-end gap-1 ml-auto"
                        >
                          <SafeIcon icon={FiDownload} /> Last ned
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="p-12 text-center text-gray-500 dark:text-gray-400">
              <SafeIcon icon={FiFileText} className="text-4xl text-gray-300 dark:text-gray-600 mx-auto mb-3" />
              <p>Ingen fakturaer funnet.</p>
            </div>
          )}
        </div>
      </div>

      {/* Cancellation Modal */}
      <AnimatePresence>
        {showCancelModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4 backdrop-blur-sm">
            <motion.div initial={{scale: 0.95, opacity: 0}} animate={{scale: 1, opacity: 1}} exit={{scale: 0.95, opacity: 0}} className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-md w-full p-6">
              <div className="text-center mb-6">
                <div className="w-16 h-16 bg-red-100 dark:bg-red-900/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <SafeIcon icon={FiAlertCircle} className="text-3xl text-red-600 dark:text-red-400" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Avslutt abonnement?</h3>
                <p className="text-gray-600 dark:text-gray-300 mt-2 text-sm">
                  Er du sikker på at du vil si opp? Du vil miste tilgang til systemet etter inneværende periode.
                </p>
              </div>
              
              <div className="bg-gray-50 dark:bg-gray-700/30 p-4 rounded-lg text-sm text-gray-700 dark:text-gray-300 mb-6 border border-gray-200 dark:border-gray-600">
                <p>Oppsigelsestid: <strong>3 måneder</strong></p>
                <p className="mt-1">Dato for opphør: <strong>{new Date(new Date().setMonth(new Date().getMonth() + 3)).toLocaleDateString()}</strong></p>
              </div>

              <div className="flex gap-3">
                <button onClick={() => setShowCancelModal(false)} className="flex-1 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 font-medium">
                  Behold
                </button>
                <button onClick={handleCancelSubscription} disabled={isProcessing} className="flex-1 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 font-medium disabled:opacity-70">
                  {isProcessing ? 'Behandler...' : 'Bekreft oppsigelse'}
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default InvoiceArchive;